import { apiFetch } from '../api/client';
import { namesClientKey } from '../data/clientKey';
import { dataViaApi } from '../api/mode';
import { chunk, executeBatch, type BatchRequest, type BatchResponse } from './batch';
import { GraphError } from './client';
import { invalidateList } from './readCache';
import { getSiteId } from './site';

/**
 * List-item writes as plain operations (Phase 6.5 step 2). Direct mode
 * turns them into Graph $batch requests exactly as before; server mode
 * (dataViaApi) sends them to /api/write, where the server checks each one
 * against the writer's role (api/src/writePolicy.ts) and runs it with its
 * own identity. Results are per operation either way — a refused or failed
 * op never throws, so callers keep deciding per item.
 */
export interface ListOp {
  /** Caller-assigned, unique within one call. */
  id: string;
  method: 'POST' | 'PATCH' | 'DELETE';
  list: string;
  /** Required for PATCH/DELETE. */
  itemId?: string;
  fields?: Record<string, unknown>;
  /** The row's eTag as read — the write is refused (412) if it changed since. */
  ifMatch?: string;
}

/** Server ops per request (= Graph's $batch limit). */
export const WRITE_LIMIT = 20;

export async function executeListOps(ops: readonly ListOp[]): Promise<Map<string, BatchResponse>> {
  if (new Set(ops.map((o) => o.id)).size !== ops.length) throw new Error('executeListOps: duplicate ids');
  const out = new Map<string, BatchResponse>();
  if (ops.length === 0) return out;
  const lists = new Set(ops.map((o) => o.list));
  for (const list of lists) invalidateList(list);
  try {
    return await runListOps(ops, out);
  } finally {
    // Again after the write: a read made while it ran must not stick.
    for (const list of lists) invalidateList(list);
  }
}

async function runListOps(ops: readonly ListOp[], out: Map<string, BatchResponse>): Promise<Map<string, BatchResponse>> {
  if (dataViaApi()) {
    for (const group of chunk(ops, WRITE_LIMIT)) {
      try {
        const res = await apiFetch<{ results: BatchResponse[] }>('/write', {
          method: 'POST',
          body: { ops: group },
          write: true,
        });
        for (const r of res.results) out.set(r.id, r);
      } catch (err) {
        // One chunk failing must not lose the results of the chunks before
        // it (created ids the caller records): its ops become failed results.
        const status = err instanceof GraphError ? err.status : 500;
        const error = {
          code: err instanceof GraphError ? err.code : 'requestFailed',
          message: err instanceof Error ? err.message : String(err),
        };
        for (const op of group) out.set(op.id, { id: op.id, status, body: { error } });
      }
    }
    return out;
  }
  const siteId = await getSiteId();
  const responses = await executeBatch(ops.map((op) => toBatchRequest(siteId, op)));
  // Direct (rollback) mode, before provisioning added ClientKey: SharePoint
  // refuses a create naming the unknown column — send those again without
  // it (no dedupe on this path; the server does that in server mode).
  const unkeyed = ops.filter((op) => {
    const res = responses.get(op.id);
    return op.method === 'POST' && op.fields?.ClientKey !== undefined && res?.status === 400 && namesClientKey(errorMessage(res));
  });
  if (unkeyed.length === 0) return responses;
  const retried = await executeBatch(unkeyed.map((op) => toBatchRequest(siteId, { ...op, fields: withoutClientKey(op.fields) })));
  for (const [id, res] of retried) responses.set(id, res);
  return responses;
}

const errorMessage = (res: BatchResponse) => (res.body as { error?: { message?: string } } | undefined)?.error?.message;

export function withoutClientKey(fields: Record<string, unknown> = {}): Record<string, unknown> {
  const out = { ...fields };
  delete out.ClientKey;
  return out;
}

function toBatchRequest(siteId: string, op: ListOp): BatchRequest {
  const base = `/sites/${siteId}/lists/${encodeURIComponent(op.list)}/items`;
  const match = op.ifMatch ? { headers: { 'If-Match': op.ifMatch } } : {};
  switch (op.method) {
    case 'POST':
      return { id: op.id, method: 'POST', url: base, body: { fields: op.fields ?? {} } };
    case 'PATCH':
      return { id: op.id, method: 'PATCH', url: `${base}/${op.itemId}/fields`, body: op.fields ?? {}, ...match };
    case 'DELETE':
      return { id: op.id, method: 'DELETE', url: `${base}/${op.itemId}`, ...match };
  }
}
